import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatTabsModule } from '@angular/material/tabs'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field'
import { MatInput, MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatNativeDateModule } from '@angular/material/core'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask'
import moment from 'moment/moment'

import { FornecedorGestaoService } from '../../../core/services/fornecedor-gestao/fornecedor-gestao.service'
import { VehicleModelService } from '../../../core/services/vehicle/vehicle-model/vehicle-model.service'
import { VehicleBrandService } from '../../../core/services/vehicle/vehicle-brand/vehicle-brand.service'
import { VehicleTypeService } from '../../../core/services/vehicle/vehicle-type/vehicle-type.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import { FornecedorVeiculoVm } from '../../../core/models/supplier-portal.model'
import { ErrorRequestModel } from '../../../core/models/response.model'

export interface AssignVehicleGestaoResult {
  success: boolean
  avisos: string[]
}

// Versão "TG operando em nome do fornecedor" do modal-assign-vehicle do Portal do
// Fornecedor — mesmas regras de negócio, mas o fornecedor vem explícito por
// {fornecedorId} (nunca do usuário logado) e não há upload de documentos aqui
// (nenhuma rota de documento foi documentada pro controller FornecedorGestao).
@Component({
  selector: 'modal-assign-vehicle-gestao',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    MatFormField, MatInput, MatLabel,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinner,
    MatTooltipModule,
    NgxMaskDirective,
  ],
  templateUrl: './modal-assign-vehicle-gestao.component.html',
  styleUrl: './modal-assign-vehicle-gestao.component.scss',
  providers: [provideNgxMask()],
})
export class ModalAssignVehicleGestaoComponent implements OnInit {
  @Output() response = new EventEmitter<AssignVehicleGestaoResult>()

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: { osId: string; fornecedorId: number; nomeFornecedor?: string },
    private dialogRef: MatDialogRef<ModalAssignVehicleGestaoComponent>,
    private fornecedorGestaoService: FornecedorGestaoService,
    private vehicleModelService: VehicleModelService,
    private vehicleBrandService: VehicleBrandService,
    private vehicleTypeService: VehicleTypeService,
    private snackbar: SnackbarCustomService,
  ) {}

  vehicleModels: any[] = []
  vehicleBrands: any[] = []
  vehicleTypes: any[] = []
  // Filtro de UI: modelo só é selecionável depois de escolher a marca — não é enviado
  // no payload, só reduz a lista do select.
  marcaControl = new FormControl<number | null>(null)

  get filteredVehicleModels(): any[] {
    const marcaId = this.marcaControl.value
    if (!marcaId) return []
    return this.vehicleModels.filter((item: any) => item.veiculoMarcaId === marcaId)
  }

  // Aba "Veículos do fornecedor"
  vehicles: FornecedorVeiculoVm[] = []
  loadingList = false
  assigningId: string | null = null

  // Aba "Cadastrar novo"
  form!: FormGroup
  creating = false

  private readonly currentYear = new Date().getFullYear()

  ngOnInit(): void {
    this.createForm()
    this.loadVehicleModels()
    this.loadVehicleBrands()
    this.loadVehicleTypes()
    this.loadVehicles()
    this.marcaControl.valueChanges.subscribe(() => {
      this.form.get('veiculoModeloId')?.setValue(null)
    })
  }

  private createForm(): void {
    this.form = this.fb.group({
      veiculoModeloId: [null, Validators.required],
      tipoVeiculoId: [null, Validators.required],
      placa: ['', Validators.required],
      cor: ['', Validators.required],
      ano: [null, [Validators.min(1950), Validators.max(this.currentYear + 1)]],
      crlv: [''],
      seguro: [''],
      seguroValidade: [''],
    })
  }

  private loadVehicleModels(): void {
    this.vehicleModelService.listVehicleModel().subscribe({
      next: (res: any) => { this.vehicleModels = res.data || [] },
    })
  }

  private loadVehicleBrands(): void {
    this.vehicleBrandService.listVehicleBrand().subscribe({
      next: (res: any) => { this.vehicleBrands = res.data || [] },
    })
  }

  private loadVehicleTypes(): void {
    this.vehicleTypeService.listVehicleType().subscribe({
      next: (res: any) => { this.vehicleTypes = res.data || [] },
    })
  }

  loadVehicles(): void {
    this.loadingList = true
    this.fornecedorGestaoService.listPagedVeiculos(this.data.fornecedorId, 1, 50).subscribe({
      next: (res: any) => { this.vehicles = res.data ?? [] },
      error: (err: ErrorRequestModel) => { this.loadingList = false; this.snackbar.errorRequest(err) },
      complete: () => { this.loadingList = false },
    })
  }

  assignExisting(veiculoId: string): void {
    this.assigningId = veiculoId
    this.fornecedorGestaoService.atribuirVeiculo(this.data.osId, veiculoId).subscribe({
      next: (res: any) => {
        this.snackbar.success(res.message)
        this.response.emit({ success: true, avisos: res.data?.avisos ?? [] })
        this.dialogRef.close()
      },
      error: (err: ErrorRequestModel) => { this.assigningId = null; this.snackbar.errorRequest(err) },
    })
  }

  createAndAssign(): void {
    if (this.form.invalid) return
    this.creating = true
    const v = this.form.value
    const model = {
      veiculoModeloId: v.veiculoModeloId,
      tipoVeiculoId: v.tipoVeiculoId,
      placa: (v.placa || '').toUpperCase(),
      cor: v.cor,
      ano: v.ano ?? null,
      crlv: v.crlv || '',
      seguro: v.seguro || '',
      seguroValidade: v.seguroValidade ? moment(v.seguroValidade).format('DD/MM/YYYY') : '',
    }
    this.fornecedorGestaoService.createVeiculo(this.data.fornecedorId, model).subscribe({
      next: (res: any) => {
        this.creating = false
        const veiculoId = res.data?.id
        if (veiculoId) {
          this.assignExisting(veiculoId)
        } else {
          this.snackbar.success(res.message)
          this.response.emit({ success: true, avisos: [] })
          this.dialogRef.close()
        }
      },
      // 409 = placa duplicada; errorRequest já extrai a mensagem do backend
      error: (err: ErrorRequestModel) => { this.creating = false; this.snackbar.errorRequest(err) },
    })
  }

  close(): void { this.dialogRef.close() }
}
